import type { User } from '@paintscout/api'
import React, { useEffect, useMemo, useRef } from 'react'
import { getEffectiveSeatType, getHighestRole, getHighestRoleName, removeAuth0Prefix } from '@paintscout/util'
import type { DropdownSelectOption, DropdownSelectProps, MultiSelectProps, SingleSelectProps } from '../DropdownSelect'
import DropdownSelect from '../DropdownSelect'
import { useUsers } from '../../providers/UsersProvider'

interface EstimatorSelectProps extends Omit<DropdownSelectProps, 'value'> {
  value: string | string[]
  userFields?: string[]
  roles?: string[]
  companyId?: string
  omit?: string[]
  includeUser?: (user: User) => boolean
  /**
   * Exclude deactivated users entirely — even when currently selected (bypasses the
   * selected-value carve-out below). Use for assignment dropdowns (crews, jobs) where a
   * deactivated user should never be selectable; leave off for filters/owner displays
   * where they must still resolve.
   */
  hideDeactivated?: boolean
  includeIds?: string[]
  onFetched?: (users: DropdownSelectOption[]) => void
  optionOverrides?: Record<string, Partial<DropdownSelectOption>>
}

export function EstimatorSelect({
  value,
  roles,
  omit,
  includeIds,
  hideDeactivated,
  variant,
  companyId: _companyIdProp,
  userFields,
  includeUser,
  onFetched,
  optionOverrides,
  ...props
}: EstimatorSelectProps) {
  const { users: contextUsers, loading, refetchUsers } = useUsers()

  const userMatchesRoles = (user: User) => {
    if (!roles) return true

    const userRoles = user.app_metadata?.roles ?? []
    if (userRoles.some((role) => roles.includes(role))) return true

    const seatType = getEffectiveSeatType({
      seatType: user.app_metadata?.seatType,
      roles: userRoles
    })

    return seatType ? roles.includes(seatType) : false
  }

  const selectedValueIds = new Set(
    (Array.isArray(value) ? value : [value]).filter(Boolean).map((id) => removeAuth0Prefix(id))
  )

  const mapUsersToOptions = (users: User[]) => {
    return [
      ...users
        .filter((user) => {
          if (!user) return false
          if (hideDeactivated && user.app_metadata?.active === false) return false
          // Always keep the currently-selected user(s) so the value still resolves (real
          // name/color/picture) even when they've been deactivated and would otherwise be
          // excluded by includeUser/role filters — otherwise the deal owner renders as a
          // blank fallback (e.g. the blue "Owned By" circle).
          if (selectedValueIds.has(removeAuth0Prefix(user.user_id))) return true
          return userMatchesRoles(user) && (includeUser ? includeUser(user) : true)
        })
        .map((user) => ({
          label: user.name,
          value: user.user_id,
          color: user.user_metadata?.color,
          inactive: user.app_metadata?.active === false,
          // Deactivated users only appear here when currently selected (kept above so the
          // value resolves) or when a consumer doesn't filter them (and doesn't pass
          // hideDeactivated) — flag them either way.
          ...(user.app_metadata?.active === false ? { sublabel: 'Deactivated' } : {}),
          role: getHighestRoleName([getHighestRole(user.app_metadata.roles)]),
          ...userFields?.reduce((acc, field) => {
            const fieldParts = field.split('.')
            const value = fieldParts.reduce((obj, part) => obj?.[part], user)
            const newKey = fieldParts[fieldParts.length - 1]
            if (newKey === 'value') {
              return { ...acc, ['customAvatar']: value }
            }
            return { ...acc, [newKey]: value }
          }, {}),
          picture: null, // this is the auth0 gravatar URL, which we don't want
          ...(optionOverrides?.[removeAuth0Prefix(user.user_id)] ?? {})
        }))
    ]
  }

  const options = useMemo(() => {
    const normalizedInclude = includeIds ? new Set(includeIds.map((id) => removeAuth0Prefix(id))) : null
    return mapUsersToOptions(contextUsers ?? []).filter(
      (option) =>
        !omit?.includes(option.value) &&
        (!normalizedInclude || normalizedInclude.has(removeAuth0Prefix(option.value) as string))
    )
  }, [contextUsers, omit, includeIds, roles, includeUser, hideDeactivated, optionOverrides, userFields, value])

  const prevContextUsersRef = useRef(contextUsers)
  useEffect(() => {
    if (!onFetched || options.length === 0) return
    if (contextUsers === prevContextUsersRef.current) return
    prevContextUsersRef.current = contextUsers
    onFetched(options)
  }, [options])

  const selectedValues: DropdownSelectOption[] = options.filter((option) =>
    (Array.isArray(value) ? value : [value]).includes(option.value)
  )

  const singleSelectProps = { ...props, value: selectedValues[0], variant: 'single' } as SingleSelectProps
  const multiSelectProps = { ...props, value: selectedValues, variant: 'multi' } as MultiSelectProps

  const selectProps = variant === 'multi' ? multiSelectProps : singleSelectProps

  useEffect(() => {
    if (typeof BroadcastChannel != 'undefined') {
      const channel = new BroadcastChannel('callout')
      const handleMessage = (event: MessageEvent) => {
        if (event.data === 'confirmed-dialog') {
          refetchUsers()
        }
      }
      channel.addEventListener('message', handleMessage)

      return () => {
        channel.removeEventListener('message', handleMessage)
        channel.close()
      }
    }
  }, [])

  return (
    <div>
      <DropdownSelect {...selectProps} disabled={props.disabled} loading={loading} options={options} />
    </div>
  )
}

export default EstimatorSelect
